import * as React from "react"
import { useEffect } from "react"
import { useIntersection, usePrevious } from "react-use"
import {
  type Insight,
  type IntervalOrigin,
  type ObjectEntities,
} from "@digits-graphql/frontend/graphql-bearer"
import { hasExactlyOne, hasFirstElement } from "@digits-shared/helpers/arrayHelper"
import colorHelper from "@digits-shared/helpers/colorHelper"
import { isMobile } from "@digits-shared/helpers/devicesHelper"
import useLazyTimeout from "@digits-shared/hooks/useLazyTimeout"
import colors from "@digits-shared/themes/colors"
import styled, { css, keyframes } from "styled-components"
import { InsightBubble, LoadingInsightBubble } from "src/shared/components/Insights/InsightBubble"

const INSIGHTS_COUNT = 6
const AUTOSCROLL_DURATION = 10000

/*
  STYLES
*/

const CarouselContainer = styled.div`
  width: 100%;
  display: flex;
  overflow: hidden;
  align-items: center;

  ::-webkit-scrollbar {
    width: 0;
    height: 0;
    background: transparent; /* Optional: just make scrollbar invisible */
  }
`

const InsightSnapContainer = styled.div`
  min-width: 100%;
  position: relative;
  scroll-snap-align: start;
`

const Indicators = styled.div`
  display: flex;
  margin: 10px auto -10px;
  justify-content: center;
`

const INDICATOR_ANIMATION = keyframes`
  from{ width: 0 ; }
  to { width: 100%; }
`

const Indicator = styled.div<{ active: boolean; paused: boolean; autoScroll: boolean }>`
  position: relative;
  width: 50px;
  height: 14px;
  margin: 0 12px;
  cursor: pointer;
  pointer-events: ${({ active }) => (active ? "none" : "auto")};

  &::before {
    content: "";
    position: absolute;
    top: 5px;
    width: 100%;
    height: 4px;
    border-radius: 4px;
    background: rgba(120, 162, 244, 0.3);
  }

  &::after {
    content: "";
    position: absolute;
    top: 5px;
    height: 4px;
    border-radius: 4px;
    width: ${({ autoScroll }) => (autoScroll ? 0 : 100)}%;
    background: ${({ active }) => (active ? colors.primary : colors.transparent)};
    will-change: width;

    ${({ active, autoScroll }) =>
      active &&
      autoScroll &&
      css`
        animation: ${INDICATOR_ANIMATION} ${AUTOSCROLL_DURATION}ms ease-in-out;
      `}

    ${({ paused }) =>
      paused &&
      css`
        animation-play-state: paused;
      `}
  }

  &:hover {
    &::before {
      background: ${colorHelper.hexToRgba(colors.primary, 0.7)};
    }
  }
`

const CarouselInsightBubble = styled(InsightBubble)`
  && {
    margin: 0;
  }
`

/*
  INTERFACES
*/

interface InsightsCarouselProps {
  origin: IntervalOrigin
  insights: Insight[] | undefined
  entities?: ObjectEntities | null
  loading?: boolean
}

interface CarouselInsightProps {
  index: number
  insight: Insight
  entities?: ObjectEntities | null
  origin: IntervalOrigin
  activeIndex: number | null
  setActiveIndex: React.Dispatch<React.SetStateAction<number | null>>
}

interface CarouselInsightWithScrollProps extends CarouselInsightProps {
  scrollBehavior: ScrollBehavior
}

interface PageIndicatorsProps {
  length: number
  activeIndex: number | null
  setActiveIndex: React.Dispatch<React.SetStateAction<number | null>>
  setAutoScroll?: React.Dispatch<React.SetStateAction<boolean>>
  carouselRef: React.RefObject<HTMLDivElement | null>
  autoScroll: boolean
}

interface PageIndicatorProps {
  index: number
  active: boolean
  lastItem: boolean
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  setAutoScroll?: React.Dispatch<React.SetStateAction<boolean>>
  carouselRef: React.RefObject<HTMLDivElement | null>
  autoScroll: boolean
}
/*
  COMPONENTS
*/

export const InsightsCarousel: React.FC<InsightsCarouselProps> = ({
  origin,
  insights: rawInsights,
  entities,
  loading,
}) => {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null)
  const prevActiveIndex = usePrevious(activeIndex)
  const ref = React.useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = React.useState(!isMobile)

  if (loading) {
    return (
      <>
        <CarouselContainer ref={ref}>
          <LoadingInsight />
        </CarouselContainer>
        <PageIndicators
          carouselRef={ref}
          length={INSIGHTS_COUNT}
          activeIndex={0}
          setActiveIndex={setActiveIndex}
          autoScroll={false}
        />
      </>
    )
  }

  const insights = rawInsights?.slice(0, INSIGHTS_COUNT)
  if (!insights?.length) return null

  return (
    <>
      <CarouselContainer ref={ref}>
        {insights.map((insight, idx) => (
          <CarouselInsight
            key={idx}
            index={idx}
            insight={insight}
            entities={entities}
            origin={origin}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            scrollBehavior={idx === 0 && prevActiveIndex === insights.length ? "auto" : "smooth"}
          />
        ))}
        {!hasExactlyOne(insights) && hasFirstElement(insights) && (
          <EOLCarouselInsight
            key={insights.length}
            index={insights.length}
            insight={insights[0]}
            entities={entities}
            origin={origin}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />
        )}
      </CarouselContainer>
      {!hasExactlyOne(insights) && (
        <PageIndicators
          carouselRef={ref}
          length={insights.length}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          autoScroll={autoScroll}
          setAutoScroll={setAutoScroll}
        />
      )}
    </>
  )
}

const CONTAINER_INTERSECTION_THRESHOLD = [1, 0.9, 0.8, 0.7, 0.6, 0.7]

const CarouselInsight: React.FC<CarouselInsightWithScrollProps> = ({
  index,
  insight,
  entities,
  origin,
  activeIndex,
  setActiveIndex,
  scrollBehavior = "smooth",
}) => {
  const ref = useScrollToInsight(activeIndex === index, scrollBehavior)

  // Create an intersection observer that fires as the item comes into or leaves view
  const observerEntry = useIntersection(ref, {
    // It's critical that this value be a constant, otherwise a new value is created
    // on each render, disrupting the hook's ability manage its effect.
    threshold: CONTAINER_INTERSECTION_THRESHOLD,
  })
  const intersectionRatio = observerEntry?.intersectionRatio ?? 0

  React.useEffect(() => {
    if (intersectionRatio === 1) {
      setActiveIndex(index)
    }
  }, [index, setActiveIndex, intersectionRatio])

  return (
    <InsightSnapContainer ref={ref}>
      <CarouselInsightBubble
        insight={insight}
        entities={entities}
        intervalOrigin={origin}
        parseDrivers={false}
      />
    </InsightSnapContainer>
  )
}

const EOLCarouselInsight: React.FC<CarouselInsightProps> = ({
  index,
  insight,
  origin,
  entities,
  activeIndex,
  setActiveIndex,
}) => {
  React.useEffect(() => {
    if (index === activeIndex) {
      setTimeout(() => setActiveIndex(0), 500)
    }
  }, [activeIndex, index, setActiveIndex])

  return (
    <CarouselInsight
      index={index}
      insight={insight}
      entities={entities}
      origin={origin}
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      scrollBehavior="smooth"
    />
  )
}

const LoadingInsight: React.FC = () => (
  <InsightSnapContainer>
    <LoadingInsightBubble />
  </InsightSnapContainer>
)

const PageIndicators: React.FC<PageIndicatorsProps> = ({
  carouselRef,
  length,
  activeIndex,
  setActiveIndex,
  autoScroll,
  setAutoScroll,
}) => (
  <Indicators>
    {Array.from({ length }).map((_, index) => (
      <PageIndicator
        key={index}
        carouselRef={carouselRef}
        index={index}
        active={index === activeIndex || (index === 0 && activeIndex === length)}
        lastItem={length === index}
        setActiveIndex={setActiveIndex}
        autoScroll={autoScroll}
        setAutoScroll={setAutoScroll}
      />
    ))}
  </Indicators>
)

const PageIndicator: React.FC<PageIndicatorProps> = ({
  carouselRef,
  index,
  active,
  lastItem,
  setActiveIndex,
  autoScroll,
  setAutoScroll,
}) => {
  const [paused, setPaused] = React.useState(false)
  const onGoToNext = React.useCallback(
    () => autoScroll && active && setActiveIndex(lastItem ? 0 : index + 1),
    [lastItem, index, active, autoScroll, setActiveIndex]
  )

  const [resumeTimer, timer, pauseTimer] = useLazyTimeout(onGoToNext, AUTOSCROLL_DURATION)

  const onClick = React.useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
    }
    setAutoScroll?.(false)
    setActiveIndex(index)
  }, [index, setActiveIndex, setAutoScroll, timer])

  const onMouseEnter = React.useCallback(() => {
    pauseTimer()
    setPaused(true)
  }, [pauseTimer])

  const onMouseLeave = React.useCallback(() => {
    resumeTimer()
    setPaused(false)
  }, [resumeTimer])

  React.useEffect(() => {
    const carousel = carouselRef.current
    if (autoScroll && active) {
      resumeTimer()

      carousel?.addEventListener("mouseenter", onMouseEnter)
      carousel?.addEventListener("mouseleave", onMouseLeave)
    }

    return () => {
      carousel?.removeEventListener("mouseenter", onMouseEnter)
      carousel?.removeEventListener("mouseleave", onMouseLeave)
    }
  }, [active, autoScroll, carouselRef, onMouseEnter, onMouseLeave, resumeTimer])

  return (
    <Indicator
      key={index}
      active={active}
      paused={paused}
      autoScroll={autoScroll}
      onClick={onClick}
    />
  )
}

function useScrollToInsight(
  isSelected: boolean,
  behavior: ScrollBehavior
): React.RefObject<HTMLDivElement> {
  const ref = React.useRef<HTMLDivElement>(null)
  const scrollToView = React.useCallback(() => {
    ref.current?.scrollIntoView({ behavior, block: "nearest" })
  }, [ref, behavior])

  const [delayedScrollTo] = useLazyTimeout(scrollToView, 1)
  useEffect(() => {
    if (isSelected) {
      delayedScrollTo()
    }
  }, [isSelected, delayedScrollTo])

  return ref as React.RefObject<HTMLDivElement>
}
